import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { X, Trophy, Calendar, Users, DollarSign, Clock, Play, CheckCircle, Image as ImageIcon } from 'lucide-react';
import Button from '../UI/Button';
import { getFullImageUrl } from '../../utils/imageUpload';
import tournamentService from '../../services/tournamentService';
import toast from 'react-hot-toast';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled(motion.div)`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 16px;
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 25px;
  border-bottom: 1px solid #2d2d44;
  background: linear-gradient(90deg, #2d2d44 0%, #1a1a2e 100%);
`;

const ModalTitle = styled.h2`
  color: #ffffff;
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #b8b8d1;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    background: #2d2d44;
    color: #ff6b6b;
  }
`;

const ModalBody = styled.div`
  padding: 25px;
`;

const TournamentImage = styled.div`
  width: 100%;
  height: 300px;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 25px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ImagePlaceholder = styled.div`
  color: white;
  font-size: 48px;
  opacity: 0.7;
`;

const TournamentInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 25px;
`;

const InfoCard = styled.div`
  background: linear-gradient(135deg, #2d2d44 0%, #1a1a2e 100%);
  border: 1px solid #2d2d44;
  border-radius: 12px;
  padding: 20px;
`;

const InfoHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
`;

const InfoIcon = styled.div`
  color: #00d4ff;
`;

const InfoLabel = styled.div`
  color: #b8b8d1;
  font-size: 14px;
  font-weight: 500;
`;

const InfoValue = styled.div`
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
`;

const Description = styled.div`
  background: linear-gradient(135deg, #2d2d44 0%, #1a1a2e 100%);
  border: 1px solid #2d2d44;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 25px;
`;

const DescriptionTitle = styled.h3`
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 15px 0;
`;

const DescriptionText = styled.p`
  color: #b8b8d1;
  line-height: 1.6;
  margin: 0;
`;

const PrizeStructure = styled.div`
  background: linear-gradient(135deg, #2d2d44 0%, #1a1a2e 100%);
  border: 1px solid #2d2d44;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 25px;
`;

const PrizeTitle = styled.h3`
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 15px 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const PrizeList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const PrizeItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  background: linear-gradient(90deg, rgba(0, 212, 255, 0.1) 0%, transparent 100%);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 8px;
`;

const PrizePosition = styled.div`
  color: #00d4ff;
  font-weight: 600;
  font-size: 16px;
`;

const PrizeAmount = styled.div`
  color: #4ecdc4;
  font-weight: 600;
  font-size: 16px;
`;

const TournamentStatus = styled.div`
  display: inline-block;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  background: ${props => {
    switch(props.status) {
      case 'active': return 'linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%)';
      case 'completed': return 'linear-gradient(135deg, #00d4ff 0%, #4ecdc4 100%)';
      case 'pending': return 'linear-gradient(135deg, #ffd93d 0%, #ff6b6b 100%)';
      default: return 'linear-gradient(135deg, #6c6c8a 0%, #4a4a6a 100%)';
    }
  }};
  color: white;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  margin-top: 25px;
  flex-wrap: wrap;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #b8b8d1;
`;

const TournamentDetailModal = ({ 
  show, 
  tournament, 
  onClose, 
  onViewBracket,
  onEditTournament 
}) => {
  const [loading, setLoading] = useState(false);
  const [tournamentDetails, setTournamentDetails] = useState(null);

  useEffect(() => {
    if (show && tournament) {
      fetchTournamentDetails();
    }
  }, [show, tournament]);

  const fetchTournamentDetails = async () => {
    try {
      setLoading(true);
      const details = await tournamentService.getTournament(tournament.id);
      setTournamentDetails(details);
    } catch (error) {
      toast.error('خطا در بارگذاری جزئیات تورنومنت');
      console.error('Error fetching tournament details:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fa-IR', {
      style: 'currency',
      currency: 'IRR',
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'نامشخص';
    try {
      return new Date(dateString).toLocaleDateString('fa-IR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'تاریخ نامعتبر';
    }
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'active': return 'در حال انجام';
      case 'completed': return 'تکمیل شده';
      case 'pending': return 'در انتظار';
      default: return 'نامشخص';
    }
  };

  if (!show) return null;

  return (
    <ModalOverlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <ModalContent
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader>
          <ModalTitle>
            <Trophy size={24} />
            {tournament?.name || 'تورنومنت'}
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          {loading ? (
            <LoadingSpinner>در حال بارگذاری جزئیات...</LoadingSpinner>
          ) : (
            <>
              {/* Tournament Image */}
              <TournamentImage>
                {tournament?.picture ? (
                  <img src={getFullImageUrl(tournament.picture)} alt={tournament.name} />
                ) : (
                  <ImagePlaceholder>
                    <Trophy size={48} />
                  </ImagePlaceholder>
                )}
              </TournamentImage>

              {/* Tournament Status */}
              <div style={{ textAlign: 'center', marginBottom: '25px' }}>
                <TournamentStatus status={tournament?.status}>
                  {getStatusText(tournament?.status)}
                </TournamentStatus>
              </div>

              {/* Tournament Info */}
              <TournamentInfo>
                <InfoCard>
                  <InfoHeader>
                    <InfoIcon><Calendar size={20} /></InfoIcon>
                    <InfoLabel>تاریخ شروع</InfoLabel>
                  </InfoHeader>
                  <InfoValue>{formatDate(tournament?.startDate)}</InfoValue>
                </InfoCard>

                <InfoCard>
                  <InfoHeader>
                    <InfoIcon><Users size={20} /></InfoIcon>
                    <InfoLabel>تعداد تیم‌ها</InfoLabel>
                  </InfoHeader>
                  <InfoValue>{tournament?.maxTeams || 'نامشخص'}</InfoValue>
                </InfoCard>

                <InfoCard>
                  <InfoHeader>
                    <InfoIcon><DollarSign size={20} /></InfoIcon>
                    <InfoLabel>ورودی</InfoLabel>
                  </InfoHeader>
                  <InfoValue>{formatCurrency(tournament?.entryFee)}</InfoValue>
                </InfoCard>

                <InfoCard>
                  <InfoHeader>
                    <InfoIcon><Clock size={20} /></InfoIcon>
                    <InfoLabel>مدت زمان</InfoLabel>
                  </InfoHeader>
                  <InfoValue>{tournament?.duration || 'نامشخص'} دقیقه</InfoValue>
                </InfoCard>
              </TournamentInfo>

              {/* Description */}
              {tournament?.description && (
                <Description>
                  <DescriptionTitle>توضیحات</DescriptionTitle>
                  <DescriptionText>{tournament.description}</DescriptionText>
                </Description>
              )}

              {/* Prize Structure */}
              {tournamentDetails?.prizeStructure && (
                <PrizeStructure>
                  <PrizeTitle>
                    <Trophy size={20} />
                    ساختار جوایز
                  </PrizeTitle>
                  <PrizeList>
                    {tournamentDetails.prizeStructure.map((prize, index) => (
                      <PrizeItem key={index}>
                        <PrizePosition>رتبه {index + 1}</PrizePosition>
                        <PrizeAmount>{formatCurrency(prize.amount)}</PrizeAmount>
                      </PrizeItem>
                    ))}
                  </PrizeList>
                </PrizeStructure>
              )}

              {/* Action Buttons */}
              <ActionButtons>
                <Button 
                  variant="outline" 
                  onClick={onClose}
                  icon={<X size={16} />}
                >
                  بستن
                </Button>
                <Button 
                  variant="primary" 
                  onClick={() => onViewBracket(tournament)}
                  icon={<Play size={16} />}
                >
                  مشاهده براکت
                </Button>
                <Button 
                  variant="secondary" 
                  onClick={() => onEditTournament(tournament)}
                  icon={<CheckCircle size={16} />}
                >
                  ویرایش
                </Button>
              </ActionButtons>
            </>
          )}
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

export default TournamentDetailModal;
